import { createContext, useCallback, useContext } from "react";

import { sortBy, uniqBy } from "lodash";
import { useSelector } from "react-redux";

import {
  getAges,
  getCategories,
  getRounds,
  getSaleSubTypes,
  getSexes,
  getSpecies,
} from "selectors";

import { LivestockSaleFieldSchema } from "./livestockSaleSchema";

const RuleFieldSchemaContext = createContext([]);
export const { Provider: RuleFieldSchema } = RuleFieldSchemaContext;

export function useRuleFieldSchema() {
  return useContext(RuleFieldSchemaContext);
}

export function useRuleFieldSchemaGenerator() {
  const ages = useSelector(getAges);
  const categories = useSelector(getCategories);
  const rounds = useSelector(getRounds);
  const sexes = useSelector(getSexes);
  const species = useSelector(getSpecies);
  const subTypes = useSelector(getSaleSubTypes);

  return useCallback(() => {
    LivestockSaleFieldSchema.find(field => field.id === "age").properties.find(
      property => typeof property === "object" && property.name === "name",
    ).set = Object.values(ages)
      .sort((a, b) => a.order - b.order)
      .map(age => [age.name, age.name]);

    LivestockSaleFieldSchema.find(
      field => field.id === "round",
    ).properties.find(
      property => typeof property === "object" && property.name === "name",
    ).set = Object.values(rounds)
      .sort((a, b) => a.order - b.order)
      .map(round => [round.name, round.name]);

    LivestockSaleFieldSchema.find(field => field.id === "sex").properties.find(
      property => typeof property === "object" && property.name === "name",
    ).set = Object.values(sexes)
      .sort((a, b) => a.order - b.order)
      .map(sex => [sex.name, sex.name]);

    LivestockSaleFieldSchema.find(field => field.id === "sale").properties.find(
      property => typeof property === "object" && property.name === "sub_type",
    ).set = uniqBy(Object.values(subTypes), "name")
      .sort((a, b) => a.order - b.order)
      .map(subType => {
        const speciesLabelText = Object.values(subTypes)
          .filter(sub => sub.name === subType.name)
          .map(sub => species[sub.speciesId]?.name)
          .sort()
          .join("/");
        return [`${speciesLabelText} - ${subType.name}`, subType.name];
      });

    LivestockSaleFieldSchema.find(
      field => field.id === "species",
    ).properties.find(
      property => typeof property === "object" && property.name === "name",
    ).set = Object.values(species)
      .sort((a, b) => a.order - b.order)
      .map(species => [species.name, species.name]);

    LivestockSaleFieldSchema.find(field => field.id === "lot").properties.find(
      property =>
        typeof property === "object" && property.name === "commercial_category",
    ).set = Object.values(categories)
      .sort((a, b) => a.order - b.order)
      .map(category => [category.name, category.name]);

    return sortBy(LivestockSaleFieldSchema, "id");
  }, [ages, categories, rounds, sexes, species, subTypes]);
}
